import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Radio, Play, Square, Pin, Zap, Users, DollarSign, Clock, Video, Plus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatMoney } from '@/lib/api';
import { Link } from 'react-router-dom';

let AgoraRTC = null;
async function loadAgora() {
    if (!AgoraRTC) { const m = await import('agora-rtc-sdk-ng'); AgoraRTC = m.default || m; }
    return AgoraRTC;
}

export default function DashboardLivestream() {
    const [lives, setLives] = useState([]);
    const [ads, setAds] = useState([]);
    const [quota, setQuota] = useState(null);
    const [openNew, setOpenNew] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', product_ids: [], quality: '720p', max_viewers: 200, enable_recording: false });
    const [broadcast, setBroadcast] = useState(null); // active broadcast state
    const [pinDialog, setPinDialog] = useState(false);
    const [pinChoice, setPinChoice] = useState({ product_id: '', discount_pct: '' });
    const localVideoRef = useRef(null);
    const clientRef = useRef(null);
    const tracksRef = useRef({ audio: null, video: null });

    const load = async () => {
        const [ls, myAds, q] = await Promise.all([
            api.get('/me/livestreams'),
            api.get('/me/ads'),
            api.get('/me/livestream/quota'),
        ]);
        setLives(ls.data || []);
        setAds(myAds.data?.items || myAds.data || []);
        setQuota(q.data);
    };
    useEffect(() => { load(); }, []);

    const createLive = async () => {
        if (!form.title || form.title.length < 3) { toast.error('Título requerido'); return; }
        try {
            const { data } = await api.post('/livestreams', form);
            toast.success('Live creado');
            setOpenNew(false); setForm({ title: '', description: '', product_ids: [], quality: '720p', max_viewers: 200, enable_recording: false });
            await load();
            return data;
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const startLive = async (live) => {
        try {
            const { data } = await api.post(`/livestreams/${live.id}/start`);
            setBroadcast({ ...data, live });
            toast.success('Live iniciado');
            await load();
            // Connect Agora broadcaster
            if (!data.mock_mode) {
                try {
                    const RTC = await loadAgora();
                    const client = RTC.createClient({ mode: 'live', codec: 'vp8' });
                    await client.setClientRole('host');
                    clientRef.current = client;
                    const audio = await RTC.createMicrophoneAudioTrack();
                    const video = await RTC.createCameraVideoTrack();
                    tracksRef.current = { audio, video };
                    if (localVideoRef.current) video.play(localVideoRef.current);
                    await client.join(data.app_id, data.channel_name, data.token, data.uid);
                    await client.publish([audio, video]);
                } catch (e) { console.error(e); toast.error('Error al conectar cámara'); }
            }
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al iniciar'); }
    };

    const endLive = async () => {
        if (!broadcast) return;
        try {
            await api.post(`/livestreams/${broadcast.livestream_id}/end`);
            // Cleanup Agora tracks
            try {
                if (tracksRef.current.audio) tracksRef.current.audio.close();
                if (tracksRef.current.video) tracksRef.current.video.close();
                if (clientRef.current) await clientRef.current.leave();
            } catch (_) {}
            toast.success('Live finalizado');
            setBroadcast(null); await load();
        } catch (e) { toast.error('Error'); }
    };

    const openPin = (live) => { setPinChoice({ product_id: live.pinned_product_id || live.product_ids?.[0] || '', discount_pct: '' }); setPinDialog(true); };
    const submitPin = async () => {
        try {
            await api.post(`/livestreams/${broadcast.livestream_id}/pin-product`, { product_id: pinChoice.product_id, discount_pct: pinChoice.discount_pct ? parseFloat(pinChoice.discount_pct) : null });
            toast.success('Producto destacado actualizado');
            setPinDialog(false); await load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-4" data-testid="dashboard-livestream">
            <div className="flex items-center gap-2">
                <Radio className="h-6 w-6 text-red-600" />
                <h1 className="text-2xl font-semibold">OkLive Shop</h1>
            </div>

            {quota && (
                <Card>
                    <CardContent className="p-4 flex flex-wrap items-center gap-4">
                        <div><div className="text-xs text-muted-foreground">Plan</div><Badge className="bg-amber-50 text-amber-800 uppercase">{quota.plan}</Badge></div>
                        <div><div className="text-xs text-muted-foreground">Minutos incluidos</div><div className="font-semibold">{quota.included_minutes}</div></div>
                        <div><div className="text-xs text-muted-foreground">Usados este mes</div><div className="font-semibold">{quota.used_minutes}</div></div>
                        <div><div className="text-xs text-muted-foreground">Disponibles</div><div className="font-semibold text-emerald-700">{quota.remaining}</div></div>
                        <div><div className="text-xs text-muted-foreground">Calidad máx.</div><div className="font-semibold">{quota.max_quality}</div></div>
                        <div><div className="text-xs text-muted-foreground">Viewers máx.</div><div className="font-semibold">{quota.max_viewers}</div></div>
                        <div className="ml-auto text-xs text-muted-foreground">Overage: ${quota.overage_per_min}/min</div>
                    </CardContent>
                </Card>
            )}

            {broadcast && (
                <Card className="border-red-500 bg-red-50/40" data-testid="live-broadcast-panel">
                    <CardHeader><CardTitle className="flex items-center gap-2"><Radio className="h-5 w-5 text-red-600 animate-pulse" />Transmitiendo</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        {broadcast.mock_mode ? (
                            <div className="aspect-video bg-gradient-to-br from-red-900 to-black rounded flex items-center justify-center text-white">
                                <div className="text-center"><Video className="h-12 w-12 mx-auto" /><p className="mt-2">Cámara desactivada (modo demo)</p><p className="text-xs opacity-70">Configura Agora en Superadmin para transmisión real</p></div>
                            </div>
                        ) : (
                            <div ref={localVideoRef} className="aspect-video bg-black rounded" data-testid="broadcaster-video" />
                        )}
                        <div className="flex flex-wrap gap-2">
                            <Button variant="outline" onClick={() => openPin(broadcast.live)} data-testid="live-pin"><Pin className="h-4 w-4 mr-1" />Destacar producto</Button>
                            <Button asChild variant="outline"><Link to={`/live/${broadcast.livestream_id}`} target="_blank"><ExternalLink className="h-4 w-4 mr-1" />Ver como espectador</Link></Button>
                            <Button onClick={endLive} className="bg-red-600 hover:bg-red-700 ml-auto" data-testid="live-end"><Square className="h-4 w-4 mr-1" />Finalizar</Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex justify-between items-center">
                <h2 className="font-semibold">Mis lives</h2>
                <Button onClick={() => setOpenNew(true)} className="bg-red-600 hover:bg-red-700" data-testid="live-new"><Plus className="h-4 w-4 mr-1" />Nuevo live</Button>
            </div>

            <div className="space-y-2">
                {lives.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Sin lives todavía. Crea uno para empezar.</div>}
                {lives.map(l => (
                    <Card key={l.id} data-testid={`live-item-${l.id}`}>
                        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <div className="font-medium">{l.title}</div>
                                <div className="text-xs text-muted-foreground">{l.product_ids?.length || 0} productos · {l.quality} · máx {l.max_viewers} viewers</div>
                                <div className="flex items-center gap-3 mt-1 text-xs">
                                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{l.peak_viewers || 0} peak</span>
                                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{l.minutes_used || 0} min</span>
                                    <span className="flex items-center gap-1"><Zap className="h-3 w-3" />{l.sales_count || 0} ventas</span>
                                    <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{formatMoney(l.sales_revenue || 0)}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className={l.status === 'live' ? 'bg-red-600 text-white animate-pulse' : l.status === 'ended' ? 'bg-slate-100 text-slate-700' : 'bg-emerald-100 text-emerald-800'}>{l.status}</Badge>
                                {l.status !== 'live' && l.status !== 'ended' && <Button size="sm" onClick={() => startLive(l)} className="bg-red-600 hover:bg-red-700" data-testid={`live-start-${l.id}`}><Play className="h-4 w-4 mr-1" />Iniciar</Button>}
                                {l.status === 'live' && !broadcast && <Button size="sm" variant="outline" onClick={() => startLive(l)} data-testid={`live-resume-${l.id}`}>Reconectar</Button>}
                                <Button asChild size="sm" variant="outline"><Link to={`/live/${l.id}`}>Ver</Link></Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* New live dialog */}
            <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Nuevo live</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="live-form-title" /></div>
                        <div><Label>Descripción</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                        <div>
                            <Label>Productos destacados</Label>
                            <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                                {ads.map(a => (
                                    <label key={a.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer hover:bg-secondary rounded px-2">
                                        <input type="checkbox" checked={form.product_ids.includes(a.id)} onChange={e => setForm({ ...form, product_ids: e.target.checked ? [...form.product_ids, a.id] : form.product_ids.filter(x => x !== a.id) })} data-testid={`live-form-product-${a.id}`} />
                                        <span className="line-clamp-1 flex-1">{a.title} — {formatMoney(a.price)}</span>
                                    </label>
                                ))}
                                {ads.length === 0 && <div className="text-xs text-muted-foreground">Sin anuncios publicados</div>}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Calidad</Label>
                                <Select value={form.quality} onValueChange={v => setForm({ ...form, quality: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="720p">720p HD</SelectItem><SelectItem value="1080p">1080p FHD (Premium)</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div><Label>Viewers máx.</Label><Input type="number" value={form.max_viewers} onChange={e => setForm({ ...form, max_viewers: parseInt(e.target.value) || 200 })} /></div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch checked={form.enable_recording} onCheckedChange={v => setForm({ ...form, enable_recording: !!v })} id="rec" />
                            <Label htmlFor="rec">Grabar y publicar replay (solo Premium)</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                        <Button onClick={createLive} className="bg-red-600 hover:bg-red-700" data-testid="live-form-submit">Crear</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Pin dialog */}
            <Dialog open={pinDialog} onOpenChange={setPinDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Destacar producto</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Producto</Label>
                            <Select value={pinChoice.product_id} onValueChange={v => setPinChoice({ ...pinChoice, product_id: v })}>
                                <SelectTrigger data-testid="pin-product-select"><SelectValue placeholder="Elige" /></SelectTrigger>
                                <SelectContent>{(broadcast?.live?.product_ids || []).map(pid => { const a = ads.find(x => x.id === pid); return <SelectItem key={pid} value={pid}>{a?.title || pid}</SelectItem>; })}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Descuento flash % (opcional)</Label><Input type="number" min="0" max="90" value={pinChoice.discount_pct} onChange={e => setPinChoice({ ...pinChoice, discount_pct: e.target.value })} data-testid="pin-discount" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPinDialog(false)}>Cancelar</Button>
                        <Button onClick={submitPin} className="bg-red-600 hover:bg-red-700" data-testid="pin-submit">Aplicar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
